import { useEffect, useState } from 'react';
import { X, AlertCircle, CheckCircle, Info, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface Toast {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  duration?: number;
}

interface ToastContainerProps {
  toasts: Toast[];
  onRemove: (id: string) => void;
}

export function ToastContainer({ toasts, onRemove }: ToastContainerProps) {
  const getIcon = (type: string) => {
    switch (type) {
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-400" />;
      case 'success':
        return <CheckCircle className="w-5 h-5 text-emerald-400" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-amber-400" />;
      default:
        return <Info className="w-5 h-5 text-blue-400" />;
    }
  };

  const getStyles = (type: string) => {
    const baseStyles = 'glass-card p-4 flex items-center gap-3 border-l-4 shadow-lg';
    switch (type) {
      case 'error':
        return `${baseStyles} border-red-500 bg-red-500/10`;
      case 'success':
        return `${baseStyles} border-emerald-500 bg-emerald-500/10`;
      case 'warning':
        return `${baseStyles} border-amber-500 bg-amber-500/10`;
      default:
        return `${baseStyles} border-blue-500 bg-blue-500/10`;
    }
  };

  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[9999] space-y-3 max-w-sm animate-fade-in">
      {toasts.map((toast) => (
        <div key={toast.id} className={cn(getStyles(toast.type), 'animate-slide-in-up')}>
          <div className="flex-shrink-0">
            {getIcon(toast.type)}
          </div>
          <p className="flex-1 text-sm font-medium text-foreground">{toast.message}</p>
          <button
            onClick={() => onRemove(toast.id)}
            className="flex-shrink-0 text-muted-foreground hover:text-foreground transition"
          >
            <X size={18} />
          </button>
        </div>
      ))}
    </div>
  );
}


